import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Container, Card, Button } from 'react-bootstrap'

interface Props {
    children: ReactNode
    fallback?: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
    errorInfo: ErrorInfo | null
}

/**
 * ErrorBoundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the app
 */
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        }
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorInfo: null,
        }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo)
        this.setState({
            error,
            errorInfo,
        })
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        })
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback
            }

            return (
                <Container className="py-5">
                    <Card className="border-danger">
                        <Card.Header className="bg-danger text-white">
                            <h4 className="mb-0">
                                <i className="fa fa-exclamation-triangle me-2"></i>
                                Something went wrong
                            </h4>
                        </Card.Header>
                        <Card.Body>
                            <p className="text-muted mb-3">
                                We&apos;re sorry, but something unexpected happened. Please try refreshing the page.
                            </p>

                            {this.state.error && (
                                <details className="mb-3">
                                    <summary className="text-danger fw-bold cursor-pointer">
                                        Error Details
                                    </summary>
                                    <pre className="bg-light p-3 mt-2 small border rounded">
                                        {this.state.error.toString()}
                                        {this.state.errorInfo?.componentStack}
                                    </pre>
                                </details>
                            )}

                            <div className="d-flex gap-2">
                                <Button variant="primary" onClick={this.handleReset}>
                                    <i className="fa fa-redo me-2"></i>
                                    Try Again
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => window.location.href = '/'}
                                >
                                    <i className="fa fa-home me-2"></i>
                                    Go Home
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Container>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
